import Breadcrumb, { Crumb, Separator } from "@asphalt-react/breadcrumb";
import Button from "@asphalt-react/button";
import Dropdown from "@asphalt-react/dropdown";
import Flag from "@asphalt-react/flag";
import Stack from "@asphalt-react/stack";
import TextField from "@asphalt-react/textfield";
import React, { useContext, useEffect, useState } from "react";
import { useParams, useHistory, Link, useLocation } from "react-router-dom";
import { UserContext } from "../../common/hooks/UserContext";
import routes from "../../common/models/route-constants";
import LoadingPage from "../../common/pages/LoadingPage";
import {
  getAnimatedIconDetails,
  updateAnimatedIcon,
} from "../api/animatedIconsApi";
import styles from "./UpdateAnimatedIconPage.module.css";
import AnimatedAssetSelector from "../../common/components/AnimatedAssetSelector";

function UpdateAnimatedIconPage() {
  const history = useHistory();
  const location = useLocation();

  // Context state
  const { user } = useContext(UserContext);

  // Input value states
  const [selectedAnimatedIconCategory, setSelectedAnimatedIconCategory] =
    useState("");
  const [selectedAnimatedIconSize, setSelectedAnimatedIconSize] = useState("");
  const [animatedIconName, setAnimatedIconName] = useState(null);
  const [animatedIconFile, setAnimatedIconFile] = useState(null);

  // Error states
  const [
    animatedIconCategoryDropdownError,
    setAnimatedIconCategoryDropdownError,
  ] = useState(null);
  const [animatedIconSizeDropdownError, setAnimatedIconSizeDropdownError] =
    useState(null);
  const [animatedIconNameError, setAnimatedIconNameError] = useState(null);
  const [assetSelectorError, setAssetSelectorError] = useState(null);

  // Icon State
  const [animatedIconDetailsObj, setAnimatedIconDetailsObj] = useState(null);

  // Page loading state
  const [isLoading, setIsLoading] = useState(true);

  // Notification state
  const [notification, setNotification] = useState(null);

  // Params state
  const { id } = useParams();

  function buildNotification() {
    return {
      title: "Animated Icon updated",
      content: `Animated Icon for ${animatedIconDetailsObj.combined_name} has been updated successfully`,
    };
  }

  const clearHistoryNotificationState = (history) => {
    let state = { ...history.location.state };
    delete state.notification;
    history.replace({
      ...history.location,
      state,
    });
  };

  // Cancel button click
  const handeCancelClick = () => {
    //TODO : Check for any changes and give user a prompt
    history.goBack();
  };

  // Save Button click
  const handleSaveClick = async () => {
    //Check if icon file is selected
    if (animatedIconFile) {
      setAssetSelectorError(null);
    } else {
      setAssetSelectorError("A file must be selected");
    }

    if (animatedIconFile) {
      setIsLoading(true);
      try {
        await updateAnimatedIcon(
          user.uid,
          id,
          animatedIconFile,
          animatedIconDetailsObj.combined_name
        );
        setIsLoading(false);
        history.push(`/${routes.ANIMATED_ASSETS}/${routes.ICONS}/${id}`, {
          notification: buildNotification(),
        });
      } catch (error) {
        setIsLoading(false);
        if (error.name === "FirebaseError") {
          console.log(error.message);
        } else if (
          error.message === "Animated Icon with the same name already exists"
        ) {
          setAnimatedIconNameError(error.message);
        }
      }
    }
  };

  useEffect(() => {
    getAnimatedIconDetails(id)
      .then((doc) => {
        setAnimatedIconDetailsObj(doc);
        setSelectedAnimatedIconCategory({
          id: "0",
          key: doc.name.animated_icon_category,
        });
        setSelectedAnimatedIconSize({
          id: "0",
          key: doc.name.animated_icon_size,
        });
        setAnimatedIconName(doc.name.animated_icon_name);
        setIsLoading(false);
      })
      .catch((error) => {
        console.log(error);
      });
    if (location.state && location["state"].notification) {
      setNotification(location.state.notification);
    }
  }, [setAnimatedIconDetailsObj, setIsLoading, id, location]);

  return (
    <React.Fragment>
      {isLoading ? (
        <LoadingPage />
      ) : (
        <React.Fragment>
          <div className={`panel ${notification ? "show" : ""}`}>
            <div className={`animateFlag ${notification ? "visible" : ""}`}>
              <Flag
                success
                floating
                title={notification ? notification.title : ""}
                content={notification ? notification.content : ""}
                onDismiss={() => {
                  setNotification(null);
                  clearHistoryNotificationState(history);
                }}
              />
            </div>
          </div>
          <div className={styles.updatePageContainer}>
            <div className={styles.breadcrumbContainer}>
              <Breadcrumb>
                <Crumb
                  text="assets"
                  tagProps={{
                    to: `/${routes.ANIMATED_ASSETS}/${routes.ICONS}`,
                  }}
                  as={Link}
                />
                <Separator />
                <Crumb
                  text={animatedIconDetailsObj.combined_name}
                  tagProps={{
                    to: `/${routes.ANIMATED_ASSETS}/${routes.ICONS}/${id}`,
                  }}
                  as={Link}
                />
              </Breadcrumb>
            </div>
            <div>
              <h3 className={styles.heading}>
                {animatedIconDetailsObj.combined_name}
              </h3>
              <h5 className={styles.subHeading}>Edit Animated Icon</h5>
              <div className={styles.formRow}>
                <div className={styles.animatedIconCategoryContainer}>
                  <p className={styles.inputLabel}>Animated Icon Category</p>
                  <div className={styles.inputContainer}>
                    <Dropdown
                      items={[selectedAnimatedIconCategory]}
                      onChange={(selectedAnimatedIconCategory) => {
                        setSelectedAnimatedIconCategory(
                          selectedAnimatedIconCategory
                        );
                        setAnimatedIconCategoryDropdownError(null);
                      }}
                      stretch
                      invalid={!!animatedIconCategoryDropdownError}
                      disabled={true}
                      initialSelectedItem={selectedAnimatedIconCategory}
                    />
                  </div>
                  {animatedIconCategoryDropdownError ? (
                    <Flag error title={animatedIconCategoryDropdownError} />
                  ) : null}
                </div>
                <div className={styles.animatedIconSizeContainer}>
                  <p className={styles.inputLabel}>Animated Icon Size</p>
                  <div className={styles.inputContainer}>
                    <Dropdown
                      items={[selectedAnimatedIconSize]}
                      onChange={(selectedAnimatedIconSize) => {
                        setSelectedAnimatedIconSize(selectedAnimatedIconSize);
                        setAnimatedIconSizeDropdownError(null);
                      }}
                      invalid={!!animatedIconSizeDropdownError}
                      stretch
                      disabled={true}
                      initialSelectedItem={selectedAnimatedIconSize}
                    />
                  </div>
                  {animatedIconSizeDropdownError ? (
                    <Flag error title={animatedIconSizeDropdownError} />
                  ) : null}
                </div>
                <div className={styles.animatedIconNameContainer}>
                  <p className={styles.inputLabel}>Animated Icon Name</p>
                  <div className={styles.inputContainer}>
                    <TextField
                      required={!!animatedIconNameError}
                      size="m"
                      placeholder="Animated Icon Name"
                      type="text"
                      disabled={true}
                      stretch
                      onChange={(e) => {
                        setAnimatedIconName(e.target.value);
                        setAnimatedIconNameError(null);
                      }}
                      pattern="[a-z\d_]+"
                      value={animatedIconName}
                    />
                  </div>
                  {animatedIconNameError ? (
                    <Flag error title={animatedIconNameError} />
                  ) : null}
                </div>
              </div>
              <div className={styles.assetSelectorContainer}>
                <div className={styles.inputContainer}>
                  <AnimatedAssetSelector
                    heading="Animated Icon File"
                    asset={animatedIconDetailsObj.img_url}
                    onAssetSelected={(file) => {
                      if (file) {
                        setAnimatedIconFile(file);
                        setAssetSelectorError(null);
                      }
                    }}
                  />
                </div>
                {assetSelectorError ? (
                  <Flag error title={assetSelectorError} />
                ) : null}
              </div>
              <Stack>
                <Button primary onClick={handleSaveClick}>
                  Save
                </Button>
                <Button secondary onClick={handeCancelClick}>
                  Cancel
                </Button>
              </Stack>
            </div>
          </div>
        </React.Fragment>
      )}
    </React.Fragment>
  );
}

export default UpdateAnimatedIconPage;
